<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';

	//Hooks
	import { beforeUpdate } from 'svelte';
	//Helpers
	import {
		get_gen_2_control_component_channel_structure,
		get_local_ip,
		input_status_check,
		is_gen_2,
		isWallDisplay,
		real_id,
		isActuator as isActuatorHelper,
		is1LWithMultipleInputs,
		isXMODDevice,
		isFanDevice,
	} from '../../../helpers/interface_helpers';
	import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	import isDimmerWithCalibration from '../../../helpers/is_calibratable_dimmer';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import {
		calculate_timer,
		clear_device_timer_state,
		convertToSeconds,
	} from '../../../helpers/timer_helpers';
	import { handleDeviceTurn } from 'src/helpers/handle_device_turn';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Stores
	import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { showCalibrationHasStartedModal } from 'src/stores/ui/calibration-modals';
	//Design system components
	import {
		Text,
		DeviceButton,
		toast,
		IconButton,
		ControlsContainer,
		DeviceButtonV2,
		type DeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	//App components
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import { doNotShowInitCalibrationModal } from 'src/stores/ui/calibration-modals';
	import { hasControlRights } from 'src/helpers/user_helpers';
	import QuickTimeControl from './QuickTimeControl.svelte';

	let className = '';
	export { className as class };

	export let status,
		device,
		settings,
		isWide = false,
		isInSlot = false,
		useV2Components = false;
	export let deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = {};

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;

	let isLoading = false;
	let ison = false;
	let localIp = false;
	let showQuickControlModal = false;
	const isGen2 = is_gen_2(device);
	const isXDevice = isXMODDevice(device?.type) || isXT1Module(device?.type);
	const noQuickControl = isWallDisplay(device?.type) || isFanDevice(device?.type);

	$: isActuator = isActuatorHelper(settings, device);
	$: component = isGen2 ? get_gen_2_control_component_channel_structure(device) : undefined;

	$: xmodComponentOutput = isXDevice && component ? status?.[component]?.output : undefined;
	$: if (
		isXDevice &&
		status?.online &&
		xmodComponentOutput !== undefined &&
		xmodComponentOutput !== ison &&
		!isLoading
	) {
		ison = xmodComponentOutput;
	}

	$: hasRights = hasControlRights(device);

	let _isDimmerWithCalibration = isDimmerWithCalibration(device.type);
	let hasTimer = false;
	let timer = { time: undefined, percentage: undefined };
	let timerStarted;
	let cacheTimerStarted;
	let timerExpectedEnd: number | undefined;
	let isonAtTimerStart: boolean | undefined; // ison captured when timer starts; used to detect stale cloud output at expiry
	let tagline = '';

	let pinLocked;
	let showModal = false; // pin unlock modal
	let inputState: 'on' | 'off';
	let disabled = false;

	function syncDeviceState() {
		const gen1Key = device.category.split('s_')[0] + 's';
		if (typeof status?.[gen1Key] !== 'undefined') {
			const ch =
				device.category === 'emeter' ? status['relays'][0] : status[gen1Key][device.channel];
			ison = ch?.ison;
			hasTimer = ch?.has_timer;
		} else if (isGen2) {
			if (!isXMODDevice(device?.type)) {
				ison =
					typeof status?.[component]?.output !== 'undefined'
						? status?.[component]?.output
						: status?.[component]?.on;
			}
			hasTimer = typeof status[component]?.timer_started_at !== 'undefined';
		} else {
			ison = false;
		}
		tagline = ison
			? $format('label_action_turn_off', { default: 'turn off' })
			: $format('label_action_turn_on', { default: 'turn on' });
	}

	function syncTimer() {
		timerStarted =
			status?.[component]?.timer_started_at ||
			status?.[device.category + 's']?.[device.channel]?.timer_started ||
			status?.['relays']?.[0]?.timer_started;
		if (typeof cacheTimerStarted === 'undefined') cacheTimerStarted = timerStarted;

		if (!(timerStarted && hasTimer)) {
			timer.time = undefined;
			timer.percentage = undefined;
			timerExpectedEnd = undefined;
			return;
		}

		// New timer — clear stale expiry gate before it can fire
		if (cacheTimerStarted !== timerStarted) {
			timerExpectedEnd = undefined;
			isonAtTimerStart = undefined;
		}

		const now = Math.floor(Date.now() / 1000);

		if (timerExpectedEnd != null && now >= timerExpectedEnd) {
			timer.time = undefined;
			timer.percentage = undefined;
			hasTimer = false;
			// If cloud output hasn't changed yet (stale), preemptively show the expected post-timer state
			if (isGen2 && isonAtTimerStart !== undefined) {
				const currentOn =
					typeof status?.[component]?.output !== 'undefined'
						? status?.[component]?.output
						: status?.[component]?.on;
				if (currentOn === isonAtTimerStart) {
					ison = !isonAtTimerStart;
				}
			}
			return;
		}

		const [, t, pct] = calculate_timer(device, status, settings);

		const timerJustStartedOrRestarted = cacheTimerStarted !== timerStarted;
		const timerDisplayEmpty = Object.values(timer).some((v) => v === undefined);

		if (timerDisplayEmpty || timerJustStartedOrRestarted) {
			if (timerJustStartedOrRestarted) cacheTimerStarted = timerStarted;
			timer.time = t;
			timer.percentage = Number(pct.toFixed(0));
			timerExpectedEnd = now + t;
			isonAtTimerStart = ison;
		}
	}

	// for devices
	beforeUpdate(() => {
		localIp = get_local_ip(device.id);
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

		if (status?.online) {
			syncDeviceState();
			syncTimer();
			if ($devices[device.id]?.input_state && !is1LWithMultipleInputs(device?.type)) {
				inputState = input_status_check(status, device) ? 'on' : 'off';
			}
			if (device.category === 'breaker') {
				// safety switch check for circuit breakers
				disabled = status?.[get_gen_2_control_component_channel_structure(device)]?.safety;
			}
		}
	});

	const handleTurn = async (flipbackSeconds: number = undefined) => {
		if (!pinLocked) {
			if (isLoading) return;
			isLoading = true;

			//in terms to know from where the calibration is started I add if statement in this component and in the DimmerCalibrateG2.svelte
			// show init calibration modal if calibration is turned on from the toggle on/off button
			// do not assign value to doNotShowInitCalibrationModal if the dimmer is calibrated
			if (
				_isDimmerWithCalibration &&
				status?.[`light:${device.channel}`]?.flags?.includes('uncalibrated')
			) {
				doNotShowInitCalibrationModal.set(false);
				showCalibrationHasStartedModal.update((state) => ({
					...state,
					channel: device.channel,
				}));
			}

			const result = await handleDeviceTurn({ device }, flipbackSeconds);

			if (result.success) {
				ison = result.expected_ison;
				if (isGen2) {
					if (typeof status?.[component]?.output !== 'undefined') {
						status[component].output = result.expected_ison;
					} else {
						status[component].on = result.expected_ison;
					}
				} else {
					if (device.category === 'emeter') {
						status['relays'][0].ison = result.expected_ison;
					} else {
						status[device.category.split('s_')[0] + 's'][device.channel].ison =
							result.expected_ison;
					}
				}

				// FE-1790: a manual turn (no `toggle_after`) cancels any running countdown on
				// the device; mirror it in the local cache so the UI timer disappears at once.
				if (!flipbackSeconds) {
					clear_device_timer_state(status, device);
					hasTimer = false;
					timerStarted = undefined;
					cacheTimerStarted = undefined;
					timer = { time: undefined, percentage: undefined };
					timerExpectedEnd = undefined;
					isonAtTimerStart = undefined;
				}

				devices_status.update_device_status({
					id: real_id(device.id),
					msg: status,
				});
			} else if ($auth_locked_devices.indexOf(real_id(device.id)) == -1) {
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			isLoading = false;
		} else {
			showModal = true;
		}
	};

	const handleEntryClick = (entry) => {
		// Send turn request with the selected timer
		const flipbackSeconds = convertToSeconds(entry?.hr || '00', entry.min, entry.sec);
		handleTurn(flipbackSeconds);

		// Close the quick control modal
		showQuickControlModal = false;
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleTurn();
	};

	const a11yProps = {
		timerAriaLabel: $format('label_turn_device', { default: 'Turn device' }),
	};
</script>

{#key timerStarted}
	{#if (isWide || isInSlot) && !useV2Components}
		<ControlsContainer
			class={classMap({ 'height-full': isInSlot })}
			color={$showNewLayout ? 'color-0' : undefined}
		>
			<svelte:fragment slot="first">
				<DeviceButton
					id="devicebutton-37tnoyt"
					{isWide}
					{isLoading}
					{hasTimer}
					{timer}
					indicator={inputState}
					isActive={ison}
					size={isWide ? 'l' : 'm'}
					icon={'fa fa-power-off'}
					aria-label={$format('label_turn_device', { default: 'Turn device' })}
					{a11yProps}
					variant="primary"
					on:click={(e) => {
						e.stopPropagation();
						handleTurn();
					}}
					disabled={!status?.online || disabled || !hasRights || isActuator}
					class={className}
				/>
				<Text styleType="controls-large" class="mt-s text-bold">{capFirstChar(tagline)}</Text>
				{#if isInSlot}
					<Text styleType="tiny" class="mt-s"
						>{capFirstChar($format('process_device', { default: 'Device' }))}</Text
					>
				{/if}
			</svelte:fragment>

			<svelte:fragment slot="topRightSlot">
				<!-- Wall display device does not support quick control -->
				<!-- Shared devices do not have access to timer controls -->
				{#if !noQuickControl && !device.shared}
					<IconButton
						aria-label={$format('label_show_quick_control', {
							default: 'Show quick control',
						})}
						shape="circle"
						variant="secondary"
						icon="far fa-clock"
						on:click={() => {
							showQuickControlModal = true;
						}}
					/>
				{/if}
			</svelte:fragment>
		</ControlsContainer>
	{:else if (isWide || isInSlot) && useV2Components}
		<ControlsContainer class={classMap({ 'height-full': isInSlot })} color="color-0">
			<svelte:fragment slot="first">
				<DeviceButtonV2
					id="devicebutton-37tnoyt"
					{isLoading}
					{hasTimer}
					{timer}
					indicator={inputState}
					isActive={ison}
					size="l"
					icon={'fa fa-power-off'}
					aria-label={$format('label_turn_device', { default: 'Turn device' })}
					{a11yProps}
					variant="primary"
					on:click={(e) => {
						e.stopPropagation();
						handleTurn();
					}}
					disabled={!status?.online || disabled || !hasRights || isActuator}
					class={className}
					{...deviceButtonV2Props}
				/>
				<Text styleType="controls-large" class="mt-s-plus text-bold">
					{capFirstChar(tagline)}
				</Text>
				{#if isInSlot}
					<Text styleType="tiny" class="mt-s">
						{capFirstChar($format('process_device', { default: 'Device' }))}
					</Text>
				{/if}
			</svelte:fragment>

			<svelte:fragment slot="topRightSlot">
				<!-- Wall display device does not support quick control -->
				{#if !noQuickControl}
					<IconButton
						aria-label={$format('label_show_quick_control', {
							default: 'Show quick control',
						})}
						shape="circle"
						variant="secondary"
						icon="far fa-clock"
						on:click={() => {
							showQuickControlModal = true;
						}}
					/>
				{/if}
			</svelte:fragment>
		</ControlsContainer>
	{:else}
		{#if useV2Components}
			<DeviceButtonV2
				id="devicebutton-137fhsf"
				aria-label={$format('label_turn_device', { default: 'Turn device' })}
				{a11yProps}
				size="l"
				{isLoading}
				{hasTimer}
				{timer}
				indicator={inputState}
				isActive={ison}
				icon={'fa fa-power-off'}
				variant="primary"
				on:click={(e) => {
					e.stopPropagation();
					handleTurn();
				}}
				disabled={!status?.online || disabled || !hasRights || isActuator}
				class={className}
				{...deviceButtonV2Props}
			/>
		{:else}
			<DeviceButton
				id="devicebutton-137fhsf"
				aria-label={$format('label_turn_device', { default: 'Turn device' })}
				{a11yProps}
				{isWide}
				{isLoading}
				{hasTimer}
				{timer}
				indicator={inputState}
				isActive={ison}
				size={isWide ? 'l' : 'm'}
				icon={'fa fa-power-off'}
				variant="primary"
				on:click={(e) => {
					e.stopPropagation();
					handleTurn();
				}}
				disabled={!status?.online || disabled || !hasRights || isActuator}
				class={className}
			/>
		{/if}
		{#if isInSlot}
			<Text styleType="controls-large" class="mt-s text-bold">{capFirstChar(tagline)}</Text>
			<Text styleType="tiny" class="mt-s"
				>{capFirstChar($format('process_device', { default: 'Device' }))}</Text
			>
		{/if}
	{/if}
{/key}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}

{#if !device.shared}
	<QuickTimeControl
		isOn={ison}
		icon="far fa-clock"
		deviceId={device.id}
		bind:showQuickControlModal
		on:select={(e) => handleEntryClick(e.detail)}
	/>
{/if}
